// Client-side HTML optimizer: shrinks embedded base64 images by re-encoding them
// to WebP with a canvas. Used when an uploaded/pasted page is over the size limit.

export const MAX_HTML_BYTES = 10 * 1024 * 1024;

export const byteSize = (str) => new Blob([str]).size;

const IMG_RE = /data:image\/(png|jpe?g|gif|bmp|webp);base64,[A-Za-z0-9+/]+={0,2}/g;

function toWebp(dataUri, { maxWidth = 1600, quality = 0.82 } = {}) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        if (width > maxWidth) { height = Math.round((height * maxWidth) / width); width = maxWidth; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/webp", quality));
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = dataUri;
  });
}

// Converts every embedded base64 image (except tiny ones) to WebP, replacing all
// occurrences. onProgress(done, total) fires as each unique image is processed.
export async function optimizeHtmlImages(html, onProgress) {
  const uniques = [...new Set((html.match(IMG_RE) || []))].filter((d) => d.length > 4000);
  const total = uniques.length;
  if (!total) return { html, converted: 0, total: 0 };
  let out = html;
  let converted = 0;
  for (let i = 0; i < uniques.length; i++) {
    const orig = uniques[i];
    const webp = await toWebp(orig);
    if (webp && webp.length < orig.length) { out = out.split(orig).join(webp); converted++; }
    onProgress?.(i + 1, total);
    // yield to the UI thread so the progress bar can paint
    await new Promise((r) => setTimeout(r, 0));
  }
  return { html: out, converted, total };
}
